import { useMemo, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client/react'
import { ArrowRight, CalendarDays, Home, Search, XCircle } from 'lucide-react'
import { Link } from 'react-router-dom'
import Button from '../components/ui/Button'
import FormError from '../components/ui/FormError'
import BookingStatusBadge from '../components/customer/BookingStatusBadge'
import {
  CANCEL_BOOKING_MUTATION,
  MY_BOOKINGS_QUERY,
  type CancelBookingData,
  type CancelBookingVariables,
  type CustomerBooking,
  type MyBookingsData,
  type MyBookingsVariables,
} from '../graphql/customer'
import { useAuth } from '../contexts/AuthContext'
import { useLang, useScopedTranslations } from '../contexts/LangContext'
import { localized } from '../lib/i18n'

const translations = {
  el: {
    eyebrow: 'Customer dashboard',
    title: 'Καλημέρα, {name}',
    body: 'Παρακολούθησε τις επόμενες κρατήσεις σου, όσα αιτήματα περιμένουν απάντηση και τα βασικά shortcuts του λογαριασμού σου.',
    loading: 'Φορτώνουμε τις κρατήσεις σου...',
    loadError: 'Δεν μπορέσαμε να φορτώσουμε το dashboard αυτή τη στιγμή.',
    upcomingTitle: 'Επόμενες κρατήσεις',
    pendingTitle: 'Αιτήματα σε αναμονή',
    emptyUpcomingTitle: 'Δεν υπάρχουν επερχόμενες επιβεβαιωμένες κρατήσεις',
    emptyUpcomingBody: 'Μόλις επιβεβαιωθεί το επόμενο ραντεβού σου, θα εμφανιστεί εδώ.',
    emptyPendingTitle: 'Δεν υπάρχουν εκκρεμή αιτήματα',
    emptyPendingBody: 'Τα νέα αιτήματα που περιμένουν απάντηση από επαγγελματία θα εμφανίζονται εδώ.',
    quickActions: 'Γρήγορες ενέργειες',
    quickSearch: 'Κλείσε καθαρισμό',
    quickSearchBody: 'Ξεκίνα νέα αναζήτηση και βρες διαθέσιμους επαγγελματίες.',
    quickProperties: 'Τα ακίνητά μου',
    quickPropertiesBody: 'Οργάνωσε διευθύνσεις, πρόσβαση και σημειώσεις για κάθε κράτηση.',
    quickBookings: 'Ιστορικό κρατήσεων',
    quickBookingsBody: 'Δες όλα τα ραντεβού και τις καταστάσεις τους.',
    provider: 'Επαγγελματίας',
    service: 'Υπηρεσία',
    schedule: 'Πρόγραμμα',
    address: 'Διεύθυνση',
    viewBooking: 'Προβολή κράτησης',
    cancelBooking: 'Ακύρωση',
    today: 'Σήμερα',
    tomorrow: 'Αύριο',
  },
  en: {
    eyebrow: 'Customer dashboard',
    title: 'Good day, {name}',
    body: 'Track your next bookings, requests waiting for a response, and the shortcuts you use most.',
    loading: 'Loading your bookings...',
    loadError: 'We could not load your dashboard right now.',
    upcomingTitle: 'Upcoming bookings',
    pendingTitle: 'Pending requests',
    emptyUpcomingTitle: 'No confirmed upcoming bookings',
    emptyUpcomingBody: 'Your next confirmed appointment will appear here once it is accepted.',
    emptyPendingTitle: 'No pending requests',
    emptyPendingBody: 'New requests waiting on a provider response will appear here.',
    quickActions: 'Quick actions',
    quickSearch: 'Book a cleaning',
    quickSearchBody: 'Start a new search and find available providers.',
    quickProperties: 'My properties',
    quickPropertiesBody: 'Manage addresses, access details, and notes for future bookings.',
    quickBookings: 'Booking history',
    quickBookingsBody: 'Review every appointment and its current status.',
    provider: 'Provider',
    service: 'Service',
    schedule: 'Schedule',
    address: 'Address',
    viewBooking: 'View booking',
    cancelBooking: 'Cancel',
    today: 'Today',
    tomorrow: 'Tomorrow',
  },
} as const

function normalizeTime(timeValue: string) {
  return timeValue.slice(0, 5)
}

function formatDate(dateValue: string, locale: 'el' | 'en', copy: { today: string; tomorrow: string }) {
  const today = new Date()
  const date = new Date(`${dateValue}T12:00:00`)
  const tomorrow = new Date()
  tomorrow.setDate(today.getDate() + 1)

  if (dateValue === today.toISOString().slice(0, 10)) return copy.today
  if (dateValue === tomorrow.toISOString().slice(0, 10)) return copy.tomorrow

  return new Intl.DateTimeFormat(locale === 'el' ? 'el-GR' : 'en-GB', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  }).format(date)
}

function providerName(booking: CustomerBooking) {
  return `${booking.providerProfile.user.firstName} ${booking.providerProfile.user.lastName}`
}

function bookingAddress(booking: CustomerBooking) {
  if (!booking.property) return null

  return [booking.property.label, booking.property.addressLine1, booking.property.city, booking.property.postalCode]
    .filter(Boolean)
    .join(', ')
}

function sortBySchedule(left: CustomerBooking, right: CustomerBooking) {
  return `${left.scheduledDate}-${left.startTime}`.localeCompare(`${right.scheduledDate}-${right.startTime}`)
}

const secondaryLinkClass = 'inline-flex items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2'

export default function CustomerDashboardPage() {
  const { currentUser } = useAuth()
  const { locale, t } = useLang()
  const copy = useScopedTranslations('customer.dashboard', translations[locale])
  const [actionErrors, setActionErrors] = useState<string[]>([])
  const [activeBookingId, setActiveBookingId] = useState<string | null>(null)

  const { data, loading, error, refetch } = useQuery<MyBookingsData, MyBookingsVariables>(MY_BOOKINGS_QUERY, {
    variables: { upcoming: true, first: 12 },
  })
  const [cancelBooking] = useMutation<CancelBookingData, CancelBookingVariables>(CANCEL_BOOKING_MUTATION)

  const bookings = data?.myBookings.nodes ?? []
  const pendingBookings = useMemo(
    () => bookings.filter((booking) => booking.status === 'pending').sort(sortBySchedule).slice(0, 3),
    [bookings],
  )
  const upcomingBookings = useMemo(
    () => bookings.filter((booking) => booking.status === 'confirmed').sort(sortBySchedule).slice(0, 3),
    [bookings],
  )

  async function handleCancel(bookingId: string) {
    if (!window.confirm(t('Να ακυρωθεί αυτή η κράτηση;', 'Cancel this booking?'))) return

    setActionErrors([])
    setActiveBookingId(bookingId)

    try {
      const { data: mutationData } = await cancelBooking({ variables: { bookingId } })
      const errors = mutationData?.cancelBooking.errors ?? [t('Δεν ήταν δυνατή η ακύρωση.', 'Could not cancel the booking.')]
      if (errors.length > 0) {
        setActionErrors(errors)
        return
      }

      await refetch()
    } catch {
      setActionErrors([t('Δεν ήταν δυνατή η ακύρωση.', 'Could not cancel the booking.')])
    } finally {
      setActiveBookingId(null)
    }
  }

  const quickActions = [
    {
      to: '/search',
      label: copy.quickSearch,
      body: copy.quickSearchBody,
      icon: <Search className="h-5 w-5" />,
    },
    {
      to: '/properties',
      label: copy.quickProperties,
      body: copy.quickPropertiesBody,
      icon: <Home className="h-5 w-5" />,
    },
    {
      to: '/bookings',
      label: copy.quickBookings,
      body: copy.quickBookingsBody,
      icon: <CalendarDays className="h-5 w-5" />,
    },
  ]

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 md:px-6 md:py-10">
      <section className="overflow-hidden rounded-[2rem] border border-primary-100 bg-[linear-gradient(135deg,#eff6ff_0%,#ffffff_55%,#f8fafc_100%)] p-6 shadow-sm md:p-8">
        <p className="text-sm font-semibold uppercase tracking-[0.22em] text-primary-600">{copy.eyebrow}</p>
        <h1 className="mt-3 text-4xl font-black tracking-tight text-slate-950 sm:text-5xl">
          {copy.title.replace('{name}', currentUser?.firstName ?? t('εκεί', 'there'))}
        </h1>
        <p className="mt-4 max-w-3xl text-lg leading-8 text-slate-600">{copy.body}</p>
      </section>

      <div className="mt-8">
        <FormError errors={actionErrors} />
        {error && (
          <div className="mt-4 rounded-xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {copy.loadError}
          </div>
        )}
      </div>

      <section className="mt-8 grid gap-8 lg:grid-cols-[1.2fr_0.8fr]">
        <div className="space-y-8">
          <div className="rounded-[1.75rem] border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between gap-4">
              <h2 className="text-2xl font-bold text-slate-950">{copy.upcomingTitle}</h2>
              <Link to="/bookings" className="text-sm font-semibold text-primary-700 hover:text-primary-800">
                {copy.viewBooking}
              </Link>
            </div>

            {loading ? (
              <p className="mt-6 text-sm text-slate-500">{copy.loading}</p>
            ) : upcomingBookings.length === 0 ? (
              <div className="mt-6 rounded-[1.5rem] border border-slate-200 bg-slate-50 p-5">
                <p className="text-lg font-semibold text-slate-950">{copy.emptyUpcomingTitle}</p>
                <p className="mt-2 text-sm leading-6 text-slate-500">{copy.emptyUpcomingBody}</p>
              </div>
            ) : (
              <div className="mt-6 space-y-4">
                {upcomingBookings.map((booking) => (
                  <article key={booking.id} className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-5">
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div>
                        <BookingStatusBadge status={booking.status} />
                        <h3 className="mt-3 text-xl font-bold text-slate-950">{localized(booking.serviceType, locale)}</h3>
                        <p className="mt-2 text-sm text-slate-600">
                          {copy.provider}: {providerName(booking)}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Link to={`/bookings/${booking.id}`} className={secondaryLinkClass}>
                          {copy.viewBooking}
                        </Link>
                        <Button
                          type="button"
                          variant="ghost"
                          loading={activeBookingId === booking.id}
                          onClick={() => handleCancel(booking.id)}
                        >
                          {copy.cancelBooking}
                        </Button>
                      </div>
                    </div>

                    <dl className="mt-5 grid gap-4 text-sm text-slate-600 md:grid-cols-2">
                      <div>
                        <dt className="font-semibold text-slate-950">{copy.schedule}</dt>
                        <dd className="mt-1">{formatDate(booking.scheduledDate, locale, copy)} · {normalizeTime(booking.startTime)} - {normalizeTime(booking.endTime)}</dd>
                      </div>
                      {bookingAddress(booking) && (
                        <div>
                          <dt className="font-semibold text-slate-950">{copy.address}</dt>
                          <dd className="mt-1">{bookingAddress(booking)}</dd>
                        </div>
                      )}
                    </dl>
                  </article>
                ))}
              </div>
            )}
          </div>

          <div className="rounded-[1.75rem] border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-2xl font-bold text-slate-950">{copy.pendingTitle}</h2>

            {loading ? (
              <p className="mt-6 text-sm text-slate-500">{copy.loading}</p>
            ) : pendingBookings.length === 0 ? (
              <div className="mt-6 rounded-[1.5rem] border border-slate-200 bg-slate-50 p-5">
                <p className="text-lg font-semibold text-slate-950">{copy.emptyPendingTitle}</p>
                <p className="mt-2 text-sm leading-6 text-slate-500">{copy.emptyPendingBody}</p>
              </div>
            ) : (
              <div className="mt-6 space-y-4">
                {pendingBookings.map((booking) => (
                  <article key={booking.id} className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-5">
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div>
                        <BookingStatusBadge status={booking.status} />
                        <h3 className="mt-3 text-lg font-bold text-slate-950">{localized(booking.serviceType, locale)}</h3>
                        <p className="mt-2 text-sm text-slate-600">{providerName(booking)}</p>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        loading={activeBookingId === booking.id}
                        onClick={() => handleCancel(booking.id)}
                      >
                        <XCircle className="h-4 w-4" />
                        {copy.cancelBooking}
                      </Button>
                    </div>

                    <p className="mt-4 text-sm text-slate-600">
                      {formatDate(booking.scheduledDate, locale, copy)} · {normalizeTime(booking.startTime)} - {normalizeTime(booking.endTime)}
                    </p>
                    <Link to={`/bookings/${booking.id}`} className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-primary-700 hover:text-primary-800">
                      {copy.viewBooking}
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </article>
                ))}
              </div>
            )}
          </div>
        </div>

        <aside className="rounded-[1.75rem] border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-2xl font-bold text-slate-950">{copy.quickActions}</h2>
          <div className="mt-6 space-y-4">
            {quickActions.map((action) => (
              <Link
                key={action.to}
                to={action.to}
                className="group block rounded-[1.5rem] border border-slate-200 bg-slate-50 p-5 transition hover:border-primary-200 hover:bg-primary-50"
              >
                <div className="flex items-start justify-between gap-4">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-primary-700 shadow-sm">
                    {action.icon}
                  </span>
                  <ArrowRight className="h-4 w-4 text-slate-400 transition group-hover:text-primary-700" />
                </div>
                <h3 className="mt-4 text-lg font-bold text-slate-950">{action.label}</h3>
                <p className="mt-2 text-sm leading-6 text-slate-600">{action.body}</p>
              </Link>
            ))}
          </div>
        </aside>
      </section>
    </div>
  )
}
